/**
 * Deep clone a nested action tree.
 * @param {Array<Object>} actions
 * @returns {Array<Object>}
 */
export const cloneActionTree = (actions = []) =>
    actions.map((action) => ({
        ...action,
        children: cloneActionTree(action.children || []),
    }))

/**
 * Re-assign sequence_id per sibling group recursively.
 * @param {Array<Object>} actions
 * @returns {Array<Object>}
 */
export const resequenceActions = (actions = []) =>
    actions.map((action, index) => ({
        ...action,
        sequence_id: index + 1,
        children: resequenceActions(action.children || []),
    }))

/**
 * Remove an action from tree by id and return removed node + next tree.
 * @param {Array<Object>} actions
 * @param {number} actionId
 * @returns {{ nextActions: Array<Object>, removedAction: Object | null }}
 */
export const removeActionById = (actions = [], actionId) => {
    for (let index = 0; index < actions.length; index += 1) {
        if (actions[index].id === actionId) {
            const nextActions = [
                ...actions.slice(0, index),
                ...actions.slice(index + 1),
            ]
            return { nextActions, removedAction: actions[index] }
        }

        const childResult = removeActionById(
            actions[index].children || [],
            actionId
        )

        if (childResult.removedAction) {
            const nextActions = [...actions]
            nextActions[index] = {
                ...actions[index],
                children: childResult.nextActions,
            }
            return {
                nextActions,
                removedAction: childResult.removedAction,
            }
        }
    }

    return { nextActions: actions, removedAction: null }
}

/**
 * Append an action as last child of a target parent.
 * @param {Array<Object>} actions
 * @param {number} parentId
 * @param {Object} actionToAppend
 * @returns {Array<Object>}
 */
export const appendAsChild = (actions = [], parentId, actionToAppend) =>
    actions.map((action) => {
        if (action.id === parentId) {
            return {
                ...action,
                children: [...(action.children || []), actionToAppend],
            }
        }

        return {
            ...action,
            children: appendAsChild(
                action.children || [],
                parentId,
                actionToAppend
            ),
        }
    })

/**
 * Find a single action by id in a nested tree.
 * @param {Array<Object>} actions
 * @param {number} actionId
 * @returns {Object | null}
 */
export const getActionById = (actions = [], actionId) => {
    for (let index = 0; index < actions.length; index += 1) {
        const action = actions[index]
        if (action.id === actionId) {
            return action
        }

        const childAction = getActionById(action.children || [], actionId)
        if (childAction) {
            return childAction
        }
    }

    return null
}

/**
 * Build an id -> action map for quick lookup.
 * @param {Array<Object>} actions
 * @param {Map<number, Object>} map
 * @returns {Map<number, Object>}
 */
export const buildActionByIdMap = (actions = [], map = new Map()) => {
    actions.forEach((action) => {
        map.set(action.id, action)
        buildActionByIdMap(action.children || [], map)
    })

    return map
}

/**
 * Collect ids for every action that has at least one child action.
 * @param {Array<Object>} actions
 * @returns {Array<number>}
 */
export const getParentActionIds = (actions = []) => {
    const parentActionIds = []

    const collect = (actionList = []) => {
        actionList.forEach((action) => {
            const children = action.children || []
            if (children.length) {
                parentActionIds.push(action.id)
                collect(children)
            }
        })
    }

    collect(actions)
    return parentActionIds
}

/**
 * Resolve the top-level ancestor id for an action id.
 * @param {Array<Object>} actions
 * @param {number} actionId
 * @returns {number | null}
 */
export const findTopLevelAncestorId = (actions = [], actionId) => {
    for (let index = 0; index < actions.length; index += 1) {
        const action = actions[index]
        if (action.id === actionId) {
            return action.id
        }

        if (getActionById(action.children || [], actionId)) {
            return action.id
        }
    }

    return null
}

/**
 * Insert action into top-level list using visible row index context.
 * @param {Array<Object>} actions
 * @param {Object} actionToInsert
 * @param {number} destinationIndex
 * @param {Array<{id: number}>} rows
 * @returns {Array<Object>}
 */
export const insertAtTopLevel = (
    actions = [],
    actionToInsert,
    destinationIndex,
    rows
) => {
    if (destinationIndex <= 0 || !rows.length) {
        return [actionToInsert, ...actions]
    }

    if (destinationIndex >= rows.length) {
        return [...actions, actionToInsert]
    }

    const previousRow = rows[destinationIndex - 1]
    const previousTopLevelId = findTopLevelAncestorId(actions, previousRow.id)

    const insertAfterIndex = actions.findIndex(
        (action) => action.id === previousTopLevelId
    )

    if (insertAfterIndex === -1) {
        return [...actions, actionToInsert]
    }

    const nextActions = [...actions]
    nextActions.splice(insertAfterIndex + 1, 0, actionToInsert)
    return nextActions
}

/**
 * Insert action under specific parent at a child index.
 * If parentId is null/undefined it inserts at top level.
 * @param {Array<Object>} actions
 * @param {number | null | undefined} parentId
 * @param {Object} actionToInsert
 * @param {number} childIndex
 * @returns {Array<Object>}
 */
export const insertAtParent = (
    actions = [],
    parentId,
    actionToInsert,
    childIndex
) => {
    if (parentId === null || parentId === undefined) {
        const nextActions = [...actions]
        const safeIndex = Math.max(0, Math.min(childIndex, nextActions.length))
        nextActions.splice(safeIndex, 0, actionToInsert)
        return nextActions
    }

    return actions.map((action) => {
        if (action.id === parentId) {
            const nextChildren = [...(action.children || [])]
            const safeIndex = Math.max(
                0,
                Math.min(childIndex, nextChildren.length)
            )
            nextChildren.splice(safeIndex, 0, actionToInsert)

            return {
                ...action,
                children: nextChildren,
            }
        }

        return {
            ...action,
            children: insertAtParent(
                action.children || [],
                parentId,
                actionToInsert,
                childIndex
            ),
        }
    })
}

/**
 * Get first/last visible row indices for a sibling set by parentId.
 * @param {Array<{parentId: number | null}>} rows
 * @param {number | null} parentId
 * @returns {{ start: number, end: number } | null}
 */
export const getSiblingRange = (rows = [], parentId) => {
    const siblingPositions = rows.reduce((acc, row, index) => {
        if (row.parentId === parentId) {
            acc.push(index)
        }

        return acc
    }, [])

    if (!siblingPositions.length) {
        return null
    }

    return {
        start: siblingPositions[0],
        end: siblingPositions[siblingPositions.length - 1],
    }
}

/**
 * Filter actions by name with case-insensitive contains query.
 * @param {Array<Object>} actions
 * @param {string} searchQuery
 * @returns {Array<Object>}
 */
export const filterActionsBySearchQuery = (actions = [], searchQuery = '') => {
    if (!searchQuery.trim()) {
        return actions
    }

    const normalizedQuery = searchQuery.trim().toLowerCase()

    return actions.filter((action) =>
        String(action.name || '')
            .toLowerCase()
            .includes(normalizedQuery)
    )
}

/**
 * Flatten currently visible tree nodes into render rows.
 * Rows include depth, display index and parent relationship metadata.
 * @param {{
 *   actions?: Array<Object>,
 *   searchQuery?: string,
 *   expandedActionIds?: Array<number>
 * }} params
 * @returns {Array<{id: number, depth: number, displayIndex: string, parentId: number | null}>}
 */
export const getVisibleRows = ({
    actions = [],
    searchQuery = '',
    expandedActionIds = [],
}) => {
    const visibleActions = filterActionsBySearchQuery(actions, searchQuery)
    const rows = []
    const expandedActionIdSet = new Set(expandedActionIds)

    const appendRows = (
        actionList,
        depth = 0,
        parentIndex = null,
        parentId = null
    ) => {
        actionList.forEach((action, index) => {
            const rowIndex = parentIndex
                ? `${parentIndex}.${index + 1}`
                : String(action.sequence_id || index + 1)

            rows.push({
                id: action.id,
                depth,
                displayIndex: rowIndex,
                parentId,
            })

            const isExpanded = expandedActionIdSet.has(action.id)
            if (isExpanded && action.children && action.children.length) {
                appendRows(action.children, depth + 1, rowIndex, action.id)
            }
        })
    }

    appendRows(visibleActions)
    return rows
}
